import type { ScanMode } from "@/hooks/usePackageScan";
import { INFLIGHT_TTL_MS, type InflightScanMarker } from "./scanInflight";

/**
 * The durable "a BATCH of scans is (or was just) in flight" marker + the pure
 * per-item restore decision — the multi-file sibling of scanInflight.ts.
 *
 * Kept as a SEPARATE localStorage key (not folded into cr-scan-inflight): the
 * lifecycles differ (single overwrites one blob; batch needs per-item
 * read-modify-write), and a corrupt batch blob must never strand single-scan
 * resume. When both markers exist (a stale leftover — starting either surface
 * clears the other going forward), pickInflightSurface arbitrates: newest wins.
 *
 * Each item's server scan survives leaving the page (the edge invoke runs to
 * completion and persists its package_scans row); what dies with the component
 * is the CLIENT pump. Two death modes matter to the restore matrix:
 *  - SPA navigation: the pump keeps running in a detached closure — queued
 *    items still start. The 5s pumpTs heartbeat proves it's alive.
 *  - Hard reload / tab close: the pump is gone. Queued items never started and
 *    their File bytes are unrecoverable → honest "lost, re-add" cards.
 */

export const BATCH_INFLIGHT_KEY = "cr-batch-inflight";
/** Heartbeat cadence for a live pump; staleness bound for "the pump is dead". */
export const BATCH_PUMP_HEARTBEAT_MS = 5_000;
export const BATCH_PUMP_STALE_MS = 15_000;
/** Marker entries are ~450 B worst case; 40 caps the blob at ~18 KB. Files past
 *  the cap scan normally — they just aren't resumable (overflow counts them). */
export const MAX_BATCH_MARKER_ITEMS = 40;

/** Coarse client-stamped lifecycle. uploading/scanning collapse to "running";
 *  server row truth outranks all of it at restore time. */
export type InflightBatchItemStatus = "queued" | "running" | "done" | "error" | "cancelled";

export interface InflightBatchItem {
  /** = MultiScanItem.id = the scan_progress row id (one uuid serves both). */
  progressId: string;
  fileName: string;
  status: InflightBatchItemStatus;
  /** Storage path of the upload — the backend keys the scan row on it, so it is
   *  the resume lookup when the row id was never learned. */
  storagePath?: string;
  /** package_scans row id; learned at buffered-response success. */
  scanId?: string;
  /** Failure message stamped by the item's catch (client view only). */
  error?: string;
  /** When the item left "queued" — anchors its 7-min give-up deadline. */
  startedTs?: number;
}

export interface InflightBatchMarker {
  v: 1;
  /** Batch-run identity — the supersession guard for every write. */
  batchId: string;
  entityType: "carrier" | "broker";
  mode?: ScanMode;
  /** Batch start (Date.now()). */
  ts: number;
  /** Detached-pump heartbeat; absent/stale means the pump is dead. */
  pumpTs?: number;
  items: InflightBatchItem[];
  /** Count of items dropped past MAX_BATCH_MARKER_ITEMS (not resumable). */
  overflow?: number;
}

function capItems(
  items: InflightBatchItem[],
  priorOverflow = 0,
): { items: InflightBatchItem[]; overflow: number } {
  const overflow = priorOverflow + Math.max(0, items.length - MAX_BATCH_MARKER_ITEMS);
  return { items: items.slice(0, MAX_BATCH_MARKER_ITEMS), overflow };
}

function write(marker: InflightBatchMarker): void {
  try {
    localStorage.setItem(BATCH_INFLIGHT_KEY, JSON.stringify(marker));
  } catch {
    /* storage unavailable */
  }
}

export function saveBatchStart(marker: InflightBatchMarker): void {
  const capped = capItems(marker.items, marker.overflow ?? 0);
  write({ ...marker, items: capped.items, ...(capped.overflow > 0 ? { overflow: capped.overflow } : {}) });
}

/** Patch ONE item's entry — but only while the marker still belongs to the same
 *  batch AND the entry exists. Late callbacks from a superseded run (the user
 *  already started another batch) must never touch the new marker; an unknown
 *  progressId (an overflow item, or a foreign scan) is a no-op. */
export function updateBatchItem(
  patch: Partial<InflightBatchItem>,
  expectedBatchId: string,
  progressId: string,
): void {
  try {
    const cur = readBatchInflight();
    if (!cur || cur.batchId !== expectedBatchId) return;
    const i = cur.items.findIndex((it) => it.progressId === progressId);
    if (i < 0) return;
    const items = cur.items.slice();
    items[i] = { ...items[i], ...patch };
    write({ ...cur, items });
  } catch {
    /* storage unavailable */
  }
}

/** Add entries for files appended into the SAME running batch. */
export function appendBatchItems(entries: InflightBatchItem[], expectedBatchId: string): void {
  try {
    const cur = readBatchInflight();
    if (!cur || cur.batchId !== expectedBatchId) return;
    const capped = capItems([...cur.items, ...entries], cur.overflow ?? 0);
    write({ ...cur, items: capped.items, ...(capped.overflow > 0 ? { overflow: capped.overflow } : {}) });
  } catch {
    /* storage unavailable */
  }
}

/** Stamp the pump heartbeat (called every BATCH_PUMP_HEARTBEAT_MS while the pump
 *  has work). Same supersession guard as every other write. */
export function stampBatchPump(expectedBatchId: string, now: number = Date.now()): void {
  try {
    const cur = readBatchInflight();
    if (!cur || cur.batchId !== expectedBatchId) return;
    write({ ...cur, pumpTs: now });
  } catch {
    /* storage unavailable */
  }
}

export function clearBatchInflight(): void {
  try {
    localStorage.removeItem(BATCH_INFLIGHT_KEY);
  } catch {
    /* storage unavailable */
  }
}

const ITEM_STATUSES: ReadonlySet<string> = new Set([
  "queued",
  "running",
  "done",
  "error",
  "cancelled",
]);

export function readBatchInflight(): InflightBatchMarker | null {
  try {
    const v = JSON.parse(localStorage.getItem(BATCH_INFLIGHT_KEY) || "null");
    if (!v || typeof v !== "object") return null;
    const o = v as Record<string, unknown>;
    // Unknown future version → treat as unreadable (never guess at a shape).
    if (o.v !== 1) return null;
    if (typeof o.batchId !== "string" || typeof o.ts !== "number" || !Array.isArray(o.items)) {
      return null;
    }
    const items = (o.items as unknown[]).filter((it): it is InflightBatchItem => {
      if (!it || typeof it !== "object") return false;
      const e = it as Record<string, unknown>;
      return (
        typeof e.progressId === "string" &&
        typeof e.fileName === "string" &&
        typeof e.status === "string" &&
        ITEM_STATUSES.has(e.status)
      );
    });
    return { ...(o as unknown as InflightBatchMarker), items };
  } catch {
    return null;
  }
}

/** Is the detached pump still provably alive? (SPA-nav case: it keeps stamping
 *  pumpTs, so queued items will still start without any page mounted.) */
export function isBatchPumpAlive(marker: InflightBatchMarker, now: number): boolean {
  return typeof marker.pumpTs === "number" && now - marker.pumpTs < BATCH_PUMP_STALE_MS;
}

// ── Restore decisions ────────────────────────────────────────────────────────

export type BatchItemRestore =
  /** Terminal non-error row → hydrated done card (rendered from the row; a
   *  purged storage_path only degrades the open-view, not the card). */
  | { kind: "done" }
  | { kind: "error"; message: string }
  /** Row pending/running — or no row yet but we hold a handle → multiplexed watch. */
  | { kind: "watch" }
  /** Queued and the pump is still alive (SPA nav) → it WILL start; watch promotes it. */
  | { kind: "pendingStart" }
  /** Queued and the pump is dead (reload) → the File bytes are gone. Honest card. */
  | { kind: "lost" }
  | { kind: "cancelled" };

const TERMINAL_OK = new Set(["extracted", "needs_review"]);

/**
 * Does a client-stamped item error look like a navigation / transport abort
 * (the page died mid-scan) rather than a real scan failure? A hard reload aborts
 * the in-flight invoke, whose rejection the item's catch stamps as an `error`,
 * even though the request was already sent and the scan finishes server-side.
 * Matches the vocabularies we actually see: supabase-js's FunctionsFetchError
 * ("Failed to send a request to the Edge Function") and the raw browser
 * transport failures behind it (Chrome "Failed to fetch", Firefox
 * "NetworkError…", Safari "Load failed", plus explicit abort phrasings).
 * Business failures (credits, validation, unreadable, not-a-packet) never match,
 * so they still surface as a real error and keep their credit-modal / retry
 * handling. Verified against a live prod hard-reload: the two uploaded items
 * carried "Failed to send a request to the Edge Function".
 */
const TRANSPORT_ABORT_RE =
  /failed to send a request|functionsfetch|failed to fetch|networkerror|network error|load failed|network request failed|the operation was aborted|the user aborted|signal is aborted|\baborted\b|err_/i;
export function isTransportAbortError(message: string | null | undefined): boolean {
  return !!message && TRANSPORT_ABORT_RE.test(message);
}

/**
 * Pure per-item mount-time decision: given one marker entry and the freshest row
 * fetch for it, what should its card restore as? Server truth outranks the
 * marker's stamps — a client transport failure can stamp `error` while the scan
 * keeps running (and finishing) server-side.
 */
export function decideBatchItemRestore(
  entry: InflightBatchItem,
  row: { status?: string | null; storage_path?: string | null; error?: string | null } | null,
  ctx: { now: number; pumpAlive: boolean },
): BatchItemRestore {
  if (row) {
    if (TERMINAL_OK.has(String(row.status))) return { kind: "done" };
    if (row.status === "error") {
      return { kind: "error", message: row.error || "The scan could not be completed." };
    }
    // pending / running (or an unknown forward-compat status) → watch it live.
    return { kind: "watch" };
  }
  // No row found for this item.
  if (entry.status === "cancelled") return { kind: "cancelled" };
  // A client `error` on an item that already UPLOADED (storagePath set) is not
  // authoritative when it looks like a navigation/transport abort: the invoke was
  // sent, so the scan is finishing server-side even though the page died. Watch
  // for its row rather than freezing the card as failed (the watcher resolves it
  // to done off the real row, or gives up honestly on its no-signal deadline).
  // Server truth outranks the marker — same principle as the row branch above.
  // Genuine failures have no storagePath OR a non-transport message, so they
  // still fall through to `error` and keep their credit-modal / retry handling.
  if (entry.storagePath && isTransportAbortError(entry.error)) return { kind: "watch" };
  if (entry.error) return { kind: "error", message: entry.error };
  // Started (or at least uploaded) but no row YET — the row claim happens after
  // upload; the watcher's no-signal give-up bounds a scan that never appears.
  if (entry.storagePath || entry.scanId || entry.startedTs || entry.status === "running" || entry.status === "done") {
    return { kind: "watch" };
  }
  // Never started. Alive pump (SPA nav) will still start it; a dead pump means
  // the File is unrecoverable.
  return ctx.pumpAlive ? { kind: "pendingStart" } : { kind: "lost" };
}

export type BatchRestoreDecision =
  | { kind: "none" }
  | { kind: "clear" }
  | { kind: "restore" };

/** Batch-level gate before any per-item work: nothing to restore, too old, or go. */
export function decideBatchRestore(
  marker: InflightBatchMarker | null,
  now: number,
): BatchRestoreDecision {
  if (!marker) return { kind: "none" };
  if (now - marker.ts > INFLIGHT_TTL_MS) return { kind: "clear" };
  if (marker.items.length === 0) return { kind: "clear" };
  return { kind: "restore" };
}

export type InflightSurface = "none" | "single" | "batch";

/**
 * When BOTH markers exist (a stale leftover — each surface clears the other's
 * marker when it starts), the one started most recently wins; the caller clears
 * the loser. Pure so the arbitration is unit-testable.
 */
export function pickInflightSurface(
  single: InflightScanMarker | null,
  batch: InflightBatchMarker | null,
): InflightSurface {
  if (!single && !batch) return "none";
  if (single && !batch) return "single";
  if (batch && !single) return "batch";
  return (batch as InflightBatchMarker).ts >= (single as InflightScanMarker).ts
    ? "batch"
    : "single";
}
